<template>
  <div class="">
    <h1>Режим приготовления</h1>

    <div v-if="isLoading" class="text-center mt-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger mt-3">
      {{ error }}
    </div>

    <div v-else-if="recipes.length === 0" class="text-center mt-4">
      <div class="card mt-4 mb-3" style="max-width: 500px; margin: auto">
        <div class="card-body">
          <h4 class="card-title">🎉 Поздравляем!</h4>
          <p class="card-text">
            Все выбранные блюда приготовлены. Надеемся, у вас всё получилось
            вкусно!
          </p>
          <hr />
          <h5 class="card-title mt-3">Поддержите Cooksy</h5>
          <p class="card-text small">
            Cooksy развивается и становится лучше благодаря вашей поддержке!
            Если вам понравилось готовить с нами, вы можете поддержать проект
            донатом. Спасибо!
          </p>
          <a
            href="https://pay.cloudtips.ru/p/13080794"
            class="btn btn-success mb-3"
            target="_blank"
            rel="noopener noreferrer"
          >
            Поддержать проект<br />
            <img
              style="padding: 10px"
              src="/img/qrCode.png"
              alt="Поддержать проект"
              width="130"
              height="130"
            />
          </a>
          <br />
          <a href="#/" class="btn btn-outline-primary">Вернуться на главную</a>
        </div>
      </div>
    </div>

    <div v-else>
      <p class="text-muted">
        Выбрано для приготовления: {{ recipes.length }}
        {{ pluralize(recipes.length, "рецепт", "рецепта", "рецептов") }}.
      </p>

      <div
        v-if="recipes.length > 1"
        class="recipe-navigation d-flex justify-content-between align-items-center mb-3"
      >
        <button
          class="btn btn-outline-secondary prev-recipe"
          @click="prevRecipe"
          :disabled="currentRecipeIndex === 0"
        >
          &lt; Предыдущий
        </button>
        <span class="text-muted">
          Рецепт {{ currentRecipeIndex + 1 }} из {{ recipes.length }}
        </span>
        <button
          class="btn btn-outline-secondary next-recipe"
          @click="nextRecipe"
          :disabled="currentRecipeIndex >= recipes.length - 1"
        >
          Следующий &gt;
        </button>
      </div>

      <!-- The main recipe card (now shows only the current recipe) -->
      <div
        v-if="currentRecipe"
        :key="currentRecipe.id"
        class="recipe-card mb-4"
      >
        <div class="card-body">
          <div
            class="accordion"
            :id="`recipeAccordion-${currentRecipe.id}`"
            v-if="currentRecipe"
          >
            <div class="accordion-item">
              <h2 class="accordion-header" :id="`heading-${currentRecipe.id}`">
                <button
                  class="accordion-button collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="`#collapse-${currentRecipe.id}`"
                  aria-expanded="false"
                  :aria-controls="`collapse-${currentRecipe.id}`"
                >
                  <span class="recipe-title-text">{{
                    currentRecipe.name
                  }}</span>
                </button>
              </h2>
              <div
                :id="`collapse-${currentRecipe.id}`"
                class="accordion-collapse collapse"
                :aria-labelledby="`heading-${currentRecipe.id}`"
                :data-bs-parent="`#recipeAccordion-${currentRecipe.id}`"
              >
                <div class="accordion-body recipe-details">
                  <!-- Image -->
                  <a :href="`#/recipes/${currentRecipe.id}`">
                    <img
                      :src="
                        $globalState.apiUrl +
                        '/image.get/' +
                        encodeURIComponent(currentRecipe.name)
                      "
                      class="img-fluid mb-3 rounded"
                    />
                  </a>

                  <!-- Details -->
                  <p v-if="currentRecipe.description">
                    <strong>Описание:</strong> {{ currentRecipe.description }}
                  </p>
                  <p>
                    <strong>Время готовки:</strong>
                    {{ currentRecipe.cookingtimeminutes }} мин
                  </p>
                  <p><strong>Порции:</strong> {{ currentRecipe.servings }}</p>

                  <!-- Ingredients List -->
                  <div
                    v-if="
                      currentRecipe.ingredients &&
                      currentRecipe.ingredients.length > 0
                    "
                  >
                    <p><strong>Ингредиенты:</strong></p>
                    <ul>
                      <li
                        v-for="ing in currentRecipe.ingredients"
                        :key="ing.name"
                        v-show="ing.quantity"
                      >
                        {{ ing.name }} — {{ ing.quantity }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Step Navigation -->
          <div
            class="d-flex justify-content-between align-items-center mt-2 mb-3"
            style="
              position: sticky;
              top: 60px;
              background: var(--bs-body-bg);
              z-index: 10;
            "
          >
            <button
              class="btn btn-secondary btn-sm"
              @click="prevStep(currentRecipeIndex)"
              :disabled="currentRecipe.currentStep === 0"
            >
              &lt; Шаг
            </button>
            <span class="text-muted small">
              Шаг {{ currentRecipe.currentStep + 1 }} /
              {{ fixedInstructions(currentRecipe).length }}
            </span>
            <button
              class="btn btn-primary btn-sm"
              @click="nextStep(currentRecipeIndex)"
              :disabled="
                currentRecipe.currentStep >=
                fixedInstructions(currentRecipe).length
              "
            >
              Шаг &gt;
            </button>
          </div>

          <!-- Instructions List -->
          <ol class="list-group list-group-flush">
            <li
              v-for="(step, stepIndex) in fixedInstructions(currentRecipe)"
              :key="stepIndex"
              class="list-group-item d-flex align-items-center"
              :class="{ active: currentRecipe.currentStep === stepIndex }"
              @click="setCurrentStep(stepIndex)"
            >
              <input
                type="checkbox"
                class="form-check-input me-3"
                :id="`step-${stepIndex}`"
                v-model="currentRecipe.stepsCompleted[stepIndex]"
                @change="checkIfAllStepsCompleted(currentRecipe)"
                @click.stop
              />
              <div class="step-content">
                <span class="step-text">
                  {{ step }}
                </span>
                <!-- AI Tip section -->
                <div class="tip-section mt-2">
                  <small
                    v-if="currentRecipe.tips && currentRecipe.tips[stepIndex]"
                    class="text-muted fst-italic"
                  >
                    {{ currentRecipe.tips[stepIndex] }}
                  </small>
                </div>
              </div>

              <button
                class="btn btn-sm btn-outline-secondary ms-auto"
                @click.stop="getAITip(step, stepIndex)"
              >
                <span
                  v-if="
                    currentRecipe.tipsLoading &&
                    currentRecipe.tipsLoading[stepIndex]
                  "
                  class="spinner-border spinner-border-sm"
                ></span>
                <i v-else class="bi bi-lightbulb"></i>
              </button>
            </li>
          </ol>

          <!-- "I Cooked This" Button -->
          <div class="text-center mt-4" v-if="currentRecipe.allStepsCompleted">
            <button
              class="btn btn-lg btn-success"
              @click="markAsCooked"
              :disabled="currentRecipe.isMarkingAsCooked"
            >
              <span
                v-if="currentRecipe.isMarkingAsCooked"
                class="spinner-border spinner-border-sm"
              ></span>
              <i v-else class="bi bi-egg-fried"></i>
              <span class="ms-2">Я приготовил!</span>
            </button>
          </div>

          <!-- Chat Toggle Button (from RecipeView) -->
          <!-- The ChatRecipe component is now self-contained. It will show its own toggle button. -->
          <div v-else>
            <hr />
            <!-- Chat Component -->
            <ChatRecipe
              v-if="currentRecipe && currentRecipe.id"
              :recipe-id="currentRecipe.id"
              title="Помощь в приготовлении"
              placeholder="Спросите о приготовлении..."
              @fullscreen-toggled="isChatFullscreenActive = $event"
              ref="chatRecipeComponent"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom styles for the accordion button to look less like a button */
.accordion-button.collapsed .recipe-title-text,
.accordion-button .recipe-title-text {
  font-size: 1.75rem; /* h3 size */
  font-weight: 500;
  color: var(--bs-body-color);
}

.accordion-button:not(.collapsed) {
  background-color: var(--bs-secondary-bg);
}

.accordion-button:focus {
  box-shadow: none;
}

.recipe-details ul {
  padding-left: 1.2rem;
}

.cooking-mode {
  max-width: 800px;
}

ol.list-group .list-group-item {
  cursor: pointer;
  transition: background-color 0.3s ease;
}

/* Override Bootstrap's default blue for .active */
ol.list-group .list-group-item.active {
  color: var(--bs-success-text-emphasis);
  background-color: var(--bs-success-bg-subtle);
  font-weight: bold;
}

/* Responsive styles for recipe navigation buttons */
/* On smaller screens, make the buttons smaller and closer to the edge */
@media (max-width: 767.98px) {
  .recipe-navigation .btn {
    padding: 0.3rem 0.5rem;
    font-size: 0.875rem;
  }
}

/* Align checkbox and text */
.list-group-item {
  display: flex;
  align-items: center;
}

.list-group-item input[type="checkbox"] {
  margin-right: 0.5rem;
}
</style>

<script>
import ChatRecipe from "../ChatRecipe.vue";
import { logEvent } from "@/services/analytics.js";

export default {
  name: "PageCookingMode",

  components: {
    ChatRecipe,
  },

  data() {
    return {
      recipes: [],
      currentRecipeIndex: 0,
      isLoading: true,
      error: null,

      isChatFullscreenActive: false, // Tracks if the chat child component is fullscreen
    };
  },
  computed: {
    currentRecipe() {
      return this.recipes.length > 0
        ? this.recipes[this.currentRecipeIndex]
        : null;
    },
  },
  watch: {
    currentRecipe(newRecipe, oldRecipe) {
      if (newRecipe && newRecipe.id !== (oldRecipe ? oldRecipe.id : null)) {
        this.checkCookedStatus(newRecipe);
      }
    },
  },
  async created() {
    const selectedIds = this.$globalState.selectedRecipes;
    if (selectedIds && selectedIds.length > 0) {
      try {
        const responseRaw = await this.sendCmd(
          "recipes.getByIds",
          JSON.stringify(selectedIds)
        );
        const responseObject = JSON.parse(responseRaw);
        const unpackedJsonString = await this.unpackData(responseObject.data);
        // Add state tracking to each recipe
        this.recipes = JSON.parse(unpackedJsonString).map((recipe) => {
          const instructions = this.getInstructionsArray(recipe.instructions);
          return {
            ...recipe,
            currentStep: 0,
            stepsCompleted: new Array(instructions.length).fill(false),
            allStepsCompleted: false,
            hasBeenCooked: false,
            isMarkingAsCooked: false,
            tips: {}, // To store fetched tips
            tipsLoading: {}, // To track loading state for each tip
          };
        });

        // Log analytics event for starting cooking mode
        if (this.$globalState.analytics && this.recipes.length > 0) {
          logEvent(this.$globalState.analytics, "tutorial_begin", {
            item_ids: this.recipes.map((r) => r.id),
          });
          console.log(
            `Analytics: Logged tutorial_begin for ${this.recipes.length} recipes.`
          );
        }

        // Initial status check for the first recipe
        if (this.recipes && this.recipes.length > 0) {
          this.checkCookedStatus(this.recipes[0]);
        }
      } catch (err) {
        console.error("Failed to fetch selected recipes:", err);
        this.error = "Не удалось загрузить выбранные рецепты.";
      } finally {
        this.isLoading = false;
      }
    } else {
      this.isLoading = false;
    }
  },
  methods: {
    nextRecipe() {
      if (this.currentRecipeIndex < this.recipes.length - 1) {
        this.currentRecipeIndex++;
      }
    },

    prevRecipe() {
      if (this.currentRecipeIndex > 0) {
        this.currentRecipeIndex--;
      }
    },

    nextStep(recipeIndex) {
      const recipe = this.recipes[recipeIndex];
      const instructionsLength = this.fixedInstructions(recipe).length;

      // When "Next Step" is clicked, it implies the current step is done.
      if (
        recipe.stepsCompleted &&
        recipe.currentStep < recipe.stepsCompleted.length
      ) {
        recipe.stepsCompleted[recipe.currentStep] = true;
        this.checkIfAllStepsCompleted(recipe);
      }

      // Then, move to the next step if we are not already on the last one.
      if (recipe.currentStep < instructionsLength - 1) {
        recipe.currentStep++;
      }
      this.scrollToActiveStep();
    },
    prevStep(recipeIndex) {
      const recipe = this.recipes[recipeIndex];
      if (recipe.currentStep > 0) {
        recipe.currentStep--;
      }
      this.scrollToActiveStep();
    },
    async checkCookedStatus(recipe) {
      // This check is crucial to prevent errors on initial load or if user is not logged in.
      if (!recipe || !recipe.id || !this.$globalState.signedIn) {
        return;
      }
      try {
        const command = `user.recipe.hasCooked/${recipe.id}`;
        const response = await this.sendCmd(command);
        const result = JSON.parse(response);
        if (result && typeof result.has_cooked === "boolean") {
          recipe.hasBeenCooked = result.has_cooked;
        }
      } catch (error) {
        // This is not a critical error, so we just log it and proceed.
        console.error(
          `Could not check cooked status for recipe ${recipe.id}:`,
          error
        );
        recipe.hasBeenCooked = false; // Default to not cooked on error
      }
    },
    async markAsCooked() {
      if (!this.currentRecipe || this.currentRecipe.isMarkingAsCooked) {
        return;
      }
      const recipeToMark = this.currentRecipe;
      const recipeIdToRemove = recipeToMark.id;

      recipeToMark.isMarkingAsCooked = true;
      try {
        const historyEntry = {
          recipe_id: recipeIdToRemove,
          action_type: "cook",
        };
        await this.sendCmd("user.history.add", JSON.stringify(historyEntry));
        await this.sendCmd(`cookinglist.remove/${recipeIdToRemove}`);

        // Remove from the global selection list
        const globalIndex =
          this.$globalState.selectedRecipes.indexOf(recipeIdToRemove);
        if (globalIndex > -1) {
          this.$globalState.selectedRecipes.splice(globalIndex, 1);
        }

        // Remove from the local component's recipe list
        this.recipes.splice(this.currentRecipeIndex, 1);

        // Adjust the current index to prevent it from going out of bounds
        if (
          this.currentRecipeIndex >= this.recipes.length &&
          this.recipes.length > 0
        ) {
          this.currentRecipeIndex = this.recipes.length - 1;
        }

        // If this was the last recipe, log tutorial completion
        if (this.recipes.length === 0) {
          if (this.$globalState.analytics) {
            logEvent(this.$globalState.analytics, "tutorial_complete");
            console.log(`Analytics: Logged tutorial_complete.`);
          }
        }
      } catch (error) {
        console.error("Error marking recipe as cooked:", error);
        // On error, at least reset the spinner on the object
        recipeToMark.isMarkingAsCooked = false;
      }
    },
    setCurrentStep(index) {
      if (this.currentRecipe) {
        this.currentRecipe.currentStep = index;
        this.scrollToActiveStep();
      }
    },
    scrollToActiveStep() {
      this.$nextTick(() => {
        const activeStepElement = this.$el.querySelector("li.active");
        if (activeStepElement) {
          activeStepElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }
      });
    },
    pluralize(count, one, few, many) {
      if (count % 10 === 1 && count % 100 !== 11) return one;
      if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100))
        return few;
      return many;
    },
    getInstructionsArray(instructions) {
      if (!instructions) {
        return [];
      }
      let instructionsString = instructions;
      if (Array.isArray(instructions)) {
        instructionsString = instructions.join("\n");
      }
      return instructionsString
        .replace(/\\\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .split("\n")
        .map((line) => line.replace(/^\d+\.\s*/, "").trim())
        .filter((line) => line.length > 0);
    },
    fixedInstructions(recipe) {
      if (!recipe || !recipe.instructions) {
        return [];
      }
      const steps = this.getInstructionsArray(recipe.instructions);
      // Initialize stepsCompleted array if it doesn't exist or is the wrong length
      if (
        !recipe.stepsCompleted ||
        recipe.stepsCompleted.length !== steps.length
      ) {
        recipe.stepsCompleted = new Array(steps.length).fill(false);
      }
      return steps;
    },
    checkIfAllStepsCompleted(recipe) {
      // Set button when all steps check boxes are checked
      recipe.allStepsCompleted = recipe.stepsCompleted.every(
        (step) => step === true
      );
    },
    async getAITip(step, stepIndex) {
      const recipe = this.currentRecipe;
      // If the tip is already loaded, toggle its visibility (remove it).
      if (recipe && recipe.tips && recipe.tips[stepIndex]) {
        recipe.tips[stepIndex] = null; // Or delete recipe.tips[stepIndex];
        return;
      }

      // If the tip is currently loading, do nothing.
      if (!recipe || (recipe.tipsLoading && recipe.tipsLoading[stepIndex])) {
        return;
      }

      recipe.tipsLoading[stepIndex] = true;

      try {
        const command = `recipe.getStepTip/${recipe.id}/${stepIndex}`;
        // The command needs a payload, so we use an empty object for GET requests
        // but pass extra data for POST-like logic.
        const payload = JSON.stringify({
          recipeName: recipe.name,
          stepText: step,
        });
        const response = await this.sendCmd(command, payload);
        const result = JSON.parse(response);
        // Correctly assign the tip to the 'tips' object using the stepIndex as the key.
        this.currentRecipe.tips[stepIndex] = result.tip_text;
      } catch (error) {
        console.error(`Error fetching AI tip for step ${stepIndex}:`, error);
        recipe.tips[stepIndex] = "Не удалось загрузить подсказку.";
      } finally {
        recipe.tipsLoading[stepIndex] = false;
      }
    },
  },
};
</script>
